import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import axios from 'helpers/api';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import AssignPatientModal from './AssignPatientModal';

const formatDate = date =>
  date ? moment(date, ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YY') : '';

const getGenderName = gender => {
  if (gender === 1 || gender === '1') return 'Male';
  if (gender === 2 || gender === '2') return 'Female';
  return 'Other';
};

/**
 * Per-caregiver page (UI-012): shows the patients assigned to a SharedLiving
 * caregiver with assign / unassign actions.
 *   GET  /athome/patients/:user_id
 *   POST /athome/unassign-patient   { user_id, patient_id }
 *   (assign handled by AssignPatientModal -> /athome/bulk-assign-patients)
 */
const SharedLivingCaregiverDetail = () => {
  const { user_id: userId } = useParams();
  const [caregiver, setCaregiver] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [unassigning, setUnassigning] = useState(null);

  const fetchPatients = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/athome/patients/${userId}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      if (data?.caregiver) setCaregiver(data.caregiver);

      const assigned = data?.patients?.data || data?.data || data?.patients || [];
      const results = (Array.isArray(assigned) ? assigned : [])
        .map(patient => {
          // UI-074 / SL-111: surface nurse + case-manager (name + phone) per
          // row. Same shape that powers MyPatientsList for caregivers.
          const nurseName = [patient.nurse_first_name, patient.nurse_last_name]
            .filter(Boolean).join(' ').trim();
          const cmName = [patient.case_manager_first_name, patient.case_manager_last_name]
            .filter(Boolean).join(' ').trim();
          return {
            id: patient.id || patient.patient_id,
            name:
              patient.name ||
              `${patient.last_name || ''}${patient.last_name && patient.first_name ? ', ' : ''}${patient.first_name || ''}`,
            dob: formatDate(patient.dob),
            gender: getGenderName(patient.gender),
            facility: patient.facility_name || '',
            relationship: patient.relationship || '',
            assignedDate: formatDate(patient.start_date || patient.assigned_at || patient.created_at),
            nurse_name: nurseName,
            nurse_phone: patient.nurse_phone || '',
            case_manager_name: cmName,
            case_manager_phone: patient.case_manager_phone || ''
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setPatients(results);
    } catch (error) {
      setPatients([]);
      toast.error('Unable to load assigned patients');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleUnassign = async patientId => {
    setUnassigning(patientId);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/athome/unassign-patient`,
        { user_id: userId, patient_id: patientId },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success('Patient unassigned successfully');
      fetchPatients();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to unassign patient');
    } finally {
      setUnassigning(null);
    }
  };

  const columns = [
    { accessorKey: 'name', header: 'Patient', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'dob', header: 'DOB', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'gender', header: 'Gender', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    { accessorKey: 'facility', header: 'Facility', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    {
      accessorKey: 'relationship',
      header: 'Relationship',
      meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } },
      cell: ({ row: { original } }) =>
        original.relationship ? (
          <Badge bg="info" className="text-capitalize">{original.relationship}</Badge>
        ) : (
          <span className="text-400">—</span>
        )
    },
    { accessorKey: 'assignedDate', header: 'Assigned', meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } } },
    {
      accessorKey: 'nurse_name',
      header: 'Nurse',
      meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } },
      cell: ({ row: { original } }) => {
        if (!original.nurse_name) return <span className="text-400">—</span>;
        return (
          <div>
            <div>{original.nurse_name}</div>
            {original.nurse_phone && (
              <a href={`tel:${original.nurse_phone}`} className="fs-11 text-decoration-none">
                {original.nurse_phone}
              </a>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'case_manager_name',
      header: 'Case Manager',
      meta: { headerProps: { className: 'text-900 text-start' }, cellProps: { className: 'text-start' } },
      cell: ({ row: { original } }) => {
        if (!original.case_manager_name) return <span className="text-400">—</span>;
        return (
          <div>
            <div>{original.case_manager_name}</div>
            {original.case_manager_phone && (
              <a href={`tel:${original.case_manager_phone}`} className="fs-11 text-decoration-none">
                {original.case_manager_phone}
              </a>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      enableSorting: false,
      meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } },
      cell: ({ row: { original } }) => (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-danger"
          disabled={unassigning === original.id}
          onClick={() => handleUnassign(original.id)}
        >
          {unassigning === original.id ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : (
            <FontAwesomeIcon icon="trash-alt" className="me-1" />
          )}
          Unassign
        </Button>
      )
    }
  ];

  const table = useAdvanceTable({
    data: patients,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const caregiverName = caregiver
    ? `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() || caregiver.username
    : 'Caregiver';

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'User Management', href: paths.viewFacility, isActive: false },
    { name: 'Caregiver Assignments', href: paths.userSharedLivingCaregiverDetail.replace(':user_id', userId), isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mt-3">
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-0">{caregiverName} — Assigned Patients</h5>
              {caregiver?.email && <small className="text-500">{caregiver.email}</small>}
            </Col>
            <Col xs="auto">
              <Button variant="primary" size="sm" onClick={() => setShowAssign(true)}>
                <FontAwesomeIcon icon="plus" className="me-2" />
                Assign Patients
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <AdvanceTableProvider {...table}>
            <Row className="mb-3">
              <Col xs="12" md="4" className="ms-auto">
                <AdvanceTableSearchBox placeholder="Search..." />
              </Col>
            </Row>
            {loading ? (
              <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : patients.length > 0 ? (
              <>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="text-nowrap align-middle"
                  tableProps={{ size: 'sm', striped: true, className: 'fs-10 mb-0 overflow-hidden' }}
                />
                <div className="mt-3"><AdvanceTableFooter rowInfo navButtons rowsPerPageSelection /></div>
              </>
            ) : (
              <div className="text-center text-danger fw-bold py-4">
                No patients assigned to this caregiver yet
              </div>
            )}
          </AdvanceTableProvider>
        </Card.Body>
      </Card>

      <AssignPatientModal
        show={showAssign}
        setShow={setShowAssign}
        userId={userId}
        alreadyAssignedIds={patients.map(p => p.id)}
        onSaved={fetchPatients}
      />
    </>
  );
};

export default SharedLivingCaregiverDetail;
